/**
 * What a next step means — the rules shared by the two screens that write one.
 *
 * There is no action record. An action is an item whose *Next steps* cell is non-empty and
 * not complete (`reghorizon/actions.py`), which is the same rule the Actions screen, the
 * monthly board paper and the morning digest all read. So creating an action is writing that
 * cell, and everything here is about what writing it implies.
 *
 * Two screens do it — the review queue's **Track + action** and the log's *Next steps* cell —
 * and they must not disagree, so the rules live in one module and `make web-check` exercises
 * them. That check is not ceremony: every wrong answer these can give is still a valid string,
 * so `tsc` sees nothing wrong with any of them.
 */

/**
 * The placeholder next step that "Track + action" writes.
 *
 * An action has to say something: a blank cell is not an action, so the button would appear
 * to have done nothing. She refines the wording in the log; the button's job is to get it on
 * the Actions screen with a date still to put against it.
 */
export const DEFAULT_NEXT_STEP = "Decide what we do about this.";

/**
 * The step "Update log + action" offers when the assistant suggested none. Mirrors
 * `UPDATE_NEXT_STEP` in the API's repository, which writes it if the box is left blank.
 */
export function updateNextStep(ukDate: string): string {
  return `Review the page as updated on ${ukDate} and decide what it means for clients.`;
}

/**
 * What writing a next step implies about the action behind it.
 *
 * Non-empty text means there is something to do, so the status is `open` — that is what puts
 * it in front of her on the Actions screen and in the weekly digest. Clearing the text clears
 * the status with it, so no row claims an open action with nothing written in it.
 *
 * Rewording a step that was marked complete reopens it, deliberately: the alternative is a
 * step she has just written that never appears anywhere, which is a silent failure. Coming
 * back is one click on Done.
 *
 * Returns null when the status already agrees with the text and nothing need be sent.
 */
export function statusForNextStep(text: string, current: string): "" | "open" | null {
  const wanted = text.trim() ? "open" : "";
  return wanted === (current || "") ? null : wanted;
}

/* --- Reading the deadline out of what she wrote --------------------------------------- */

/**
 * Why this is a matcher and not a model call.
 *
 * `enrich/deadline.py` asks Sonnet to read the *existing* 138 notes, and it is right to: those
 * were written years apart with no field to put a date in, and the question there is
 * comprehension. This is a different question. She is typing a next step *now*, and what she
 * types is short, forward-looking and usually names its date plainly. A matcher answers it in
 * no time, for nothing, with no round trip — and, more to the point, it answers the same way
 * every time, which a model does not.
 *
 * The same argument `assessment/anchor.py` makes: a date is what a matcher finds reliably and
 * a model paraphrases plausibly, and **a wrong deadline is worse than none** because she plans
 * around it. So this reads her words rather than interpreting them, and it declines whenever
 * the answer is not obvious. Declining costs her one click on the Actions screen; being wrong
 * costs her a missed consultation.
 */
const MONTHS: Record<string, number> = {
  january: 1, jan: 1,
  february: 2, feb: 2,
  march: 3, mar: 3,
  april: 4, apr: 4,
  may: 5,
  june: 6, jun: 6,
  july: 7, jul: 7,
  august: 8, aug: 8,
  september: 9, sep: 9, sept: 9,
  october: 10, oct: 10,
  november: 11, nov: 11,
  december: 12, dec: 12,
};

const MONTH_NAMES = Object.keys(MONTHS).join("|");
const ORDINAL = "(?:st|nd|rd|th)?";

/**
 * The words that make a date a deadline rather than a date.
 *
 * "Takes effect" counts — `deadline.py` says the operative deadline is "the date something
 * must be done by: responses close, a rule takes effect, a firm must have acted". Bare "on"
 * is deliberately absent: "published on 3 March" is not a deadline, and "closes on" is caught
 * by the optional preposition after the cue.
 */
const CUE = new RegExp(
  "(?:by|before|due|deadline|closes?|closing|ends?|expires?|no later than|not later than|" +
    "on or before|until|till|effective|in force|comes into force|applies|starts?|commences?)" +
    "\\s*(?:on|from|by)?\\W*$",
  "i",
);

/**
 * Words that make a date explicitly *not* a deadline. Checked after the cue and beating it,
 * because "published on" ends in a preposition and would otherwise read as one.
 *
 * "Updated", "revised" and "amended" joined on 16 September 2026, when the review queue began
 * offering "Review the page as updated on 01/09/2026" as an action: read without them, the
 * date a page changed became the date the action was due — already past on the day it was
 * written, so every such action would have opened straight into Overdue.
 */
const ANTI_CUE =
  /(?:published|issued|released|dated|announced|came into force|consulted|updated|revised|amended)\s*(?:on|in)?\W*$/i;

/** How far back to look for the cue. Long enough for "responses are required by". */
const CUE_WINDOW = 40;

interface Candidate {
  iso: string;
  at: number;
  end: number;
  text: string;
  cued: boolean;
  anti: boolean;
}

/** Today, in *her* calendar. `toISOString` is UTC and is a day out for the hour before midnight. */
export function todayISO(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** An ISO date, or "" if those numbers are not a real day — 31 February and friends. */
function iso(year: number, month: number, day: number): string {
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  const made = new Date(Date.UTC(year, month - 1, day));
  if (made.getUTCMonth() !== month - 1 || made.getUTCDate() !== day) return "";
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * A day and month with no year: the next time that date comes round.
 *
 * A next step is a thing still to do, so its deadline is ahead — that is what "respond by 3
 * September", written today, means to anyone reading it. If this year's has gone, she means
 * next year's.
 */
function withoutYear(month: number, day: number, today: string): string {
  const thisYear = iso(Number(today.slice(0, 4)), month, day);
  if (!thisYear) return "";
  return thisYear >= today ? thisYear : iso(Number(today.slice(0, 4)) + 1, month, day);
}

function push(found: Candidate[], text: string, at: number, end: number, value: string): void {
  if (!value) return;
  // A date inside another already-matched date — "3 September" inside "3 September 2026" — is
  // the same date read twice, and it would look like two candidates and force a refusal.
  if (found.some((c) => at < c.end && end > c.at)) return;
  const before = text.slice(Math.max(0, at - CUE_WINDOW), at);
  found.push({
    iso: value,
    at,
    end,
    text: text.slice(at, end),
    anti: ANTI_CUE.test(before),
    cued: CUE.test(before),
  });
}

/**
 * Every date in the text, in the order they appear.
 *
 * Year-bearing forms are read first so that the yearless ones cannot claim part of them.
 * Two-digit years are not accepted at all: "15/09/26" could be 2026 or the fifteenth of a
 * reference number, and guessing is exactly what this must not do.
 */
function candidates(text: string, today: string): Candidate[] {
  const found: Candidate[] = [];
  const scan = (pattern: RegExp, read: (m: RegExpExecArray) => string) => {
    const re = new RegExp(pattern.source, pattern.flags.includes("g") ? pattern.flags : pattern.flags + "g");
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
      push(found, text, match.index, match.index + match[0].length, read(match));
    }
  };

  // ISO, as the API stores it.
  scan(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (m) => iso(+m[1], +m[2], +m[3]));
  // UK numeric: day first, always. A four-digit year is required, which is also what keeps
  // "CP26/12" and "COBS 2.2A" from reading as dates.
  scan(/\b(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})\b/g, (m) => iso(+m[3], +m[2], +m[1]));
  // "15 September 2026", "15th Sept 2026"
  scan(
    new RegExp(`\\b(\\d{1,2})${ORDINAL}\\s+(${MONTH_NAMES})\\.?\\s*,?\\s*(\\d{4})\\b`, "gi"),
    (m) => iso(+m[3], MONTHS[m[2].toLowerCase()], +m[1]),
  );
  // "September 15, 2026"
  scan(
    new RegExp(`\\b(${MONTH_NAMES})\\.?\\s+(\\d{1,2})${ORDINAL}\\s*,?\\s*(\\d{4})\\b`, "gi"),
    (m) => iso(+m[3], MONTHS[m[1].toLowerCase()], +m[2]),
  );
  // The same two without a year.
  scan(
    new RegExp(`\\b(\\d{1,2})${ORDINAL}\\s+(${MONTH_NAMES})\\b`, "gi"),
    (m) => withoutYear(MONTHS[m[2].toLowerCase()], +m[1], today),
  );
  scan(
    new RegExp(`\\b(${MONTH_NAMES})\\.?\\s+(\\d{1,2})${ORDINAL}\\b`, "gi"),
    (m) => withoutYear(MONTHS[m[1].toLowerCase()], +m[2], today),
  );

  return found.sort((a, b) => a.at - b.at);
}

/** What was read out of her note, and the words it was read from. */
export interface ReadDate {
  date: string;
  basis: string;
}

/**
 * The deadline in a next step she has just written, or null.
 *
 * Null is a perfectly good answer and the common one — she gets the Actions screen's date
 * picker, which is where she was going anyway. The three ways to get a date:
 *
 * - one date in the note, and nothing marking it as a publication date;
 * - several, but only the ones behind a deadline cue ("by", "closes", "no later than") count,
 *   and they agree;
 * - several cued dates that are all the same day, written more than once.
 *
 * Anything else is a refusal, because picking between two dates she wrote is guessing at which
 * one she meant, and this must never do that.
 */
export function dateFromNextStep(text: string, today: string = todayISO()): ReadDate | null {
  const pool = candidates(text || "", today).filter((c) => !c.anti);
  if (!pool.length) return null;

  const cued = pool.filter((c) => c.cued);
  const considered = cued.length ? cued : pool;
  const distinct = new Set(considered.map((c) => c.iso));
  if (distinct.size !== 1) return null;
  // With no cue anywhere, one date is her date; more than one and there is nothing to choose by.
  if (!cued.length && pool.length !== 1) return null;

  return { date: considered[0].iso, basis: considered[0].text };
}

/**
 * The patch that writing a next step implies, beyond the text itself.
 *
 * `current` is the row as it stands. An existing `due_date` is never overwritten — she set it,
 * and a date she chose losing to a date a matcher found in a sentence is the one outcome worth
 * refusing outright. Filling an empty one is the whole point.
 */
export function actionPatchFor(
  text: string,
  current: { next_steps_status?: string; due_date?: string },
  today: string = todayISO(),
): { next_steps_status?: "" | "open"; due_date?: string; read?: ReadDate } {
  const patch: { next_steps_status?: "" | "open"; due_date?: string; read?: ReadDate } = {};

  const status = statusForNextStep(text, current.next_steps_status || "");
  if (status !== null) patch.next_steps_status = status;

  if (text.trim() && !(current.due_date || "").trim()) {
    const read = dateFromNextStep(text, today);
    if (read) {
      patch.due_date = read.date;
      patch.read = read;
    }
  }
  return patch;
}

/**
 * The date an update's action carries — "Update log + action" on a revised page.
 *
 * Unlike `actionPatchFor`, this step *replaces* the one on the item, so the date goes with the
 * words that are now in the cell:
 *
 * - a date read out of the new step is used — her words, on screen before she presses;
 * - otherwise a date that belonged to a step already marked complete (or to no step at all)
 *   is cleared, because it describes finished work and, reopened, would read as overdue — the
 *   17 April consultation date on a page revised in September;
 * - otherwise her date stands: she set it against an open step, and nothing here knows better.
 *
 * Returns `due_date` only when something should be sent: absent leaves hers alone.
 */
export function updateActionDate(
  step: string,
  current: { next_steps?: string; next_steps_status?: string; due_date?: string },
  today: string = todayISO(),
): { due_date?: string; read?: ReadDate } {
  const read = dateFromNextStep(step, today);
  if (read) return { due_date: read.date, read };
  const hadOpenStep =
    Boolean((current.next_steps || "").trim()) && current.next_steps_status !== "complete";
  if ((current.due_date || "").trim() && !hadOpenStep) return { due_date: "" };
  return {};
}

/**
 * What an action is, and how much it matters. **Mirrors `reghorizon/actions.py`.**
 *
 * There is still no action record: an action is an item whose Next steps cell is non-empty and
 * not complete. Both rules live here rather than in the screen that happens to need them,
 * because on 1 September 2026 the Reports screen started needing them too — its impact-assessment
 * picker now offers the HIGH-priority actions rather than everything flagged "Review impact" —
 * and a second copy is how the Actions header came to disagree with its own bands.
 */
export function isAction(item: {
  next_steps?: string;
  next_steps_status?: string;
  status?: string;
}): boolean {
  // An item she has stopped tracking carries no work, whatever its cell still says.
  if (item.status === "archived") return false;
  return Boolean((item.next_steps || "").trim()) && item.next_steps_status !== "complete";
}

/**
 * Unset reads as medium and is never stored as medium: every item predating the field is unset,
 * and writing "medium" across the log would claim she had triaged things she has never opened.
 */
export function priorityOf(item: { priority?: string }): "high" | "medium" | "low" {
  const value = (item.priority || "").toLowerCase();
  return value === "high" || value === "low" ? value : "medium";
}
